import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormItemWrapper, FormWrapper, TwoRow, WhiteWrapper } from './apiKey.styled'
import { Button, ConfigProvider, Form, Input, Modal, Radio, Select, Spin, Tooltip } from 'antd'
import { CopyOutlined, ReloadOutlined } from '@ant-design/icons'
import { fetchMappingKeys } from '../../apis/source'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { fetchApiKey, fetchApiKeyPermissions, generateApiKey, regenerateApiKey, revokeApiKey } from '../../apis/apiKey'

function ApiKey (props) {
  const { adminMode } = props
  const { t: ln_t } = useTranslation()
  const user = useSelector((state) => state.user)
  const { nowGroup, nowBrand, groups } = user || {}
  const [form] = Form.useForm()
  const [apiPermissionMapping, setApiPermissionMapping] = useState({})
  const [mappingOpt, setMappingOpt] = useState([])
  const [enable, setEnable] = useState(0)
  const [keyData, setKeyData] = useState()
  const [expired, setExpired] = useState()
  const { apiKeyPermissions = '' } = groups.find(i => i.id === nowGroup) || {}

  const defPlaceholder = (name, type) => {
    const message = type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''
    return `${message} ${ln_t(`apiKey.${name}`)}`
  }
  const defConfig = (name, type, required = true) => ({ name, label: ln_t(`apiKey.${name}`), rules: [{ required, message: defPlaceholder(name, type) }] })

  const statusOpt = [{ value: 1, label: ln_t('util.enable') }, { value: 0, label: ln_t('util.disable') }]

  const formatDate = date => date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : null
  const getApiKey = async () => {
    const res = await fetchApiKey(nowBrand)
    if (res?.status === 200) {
      const { secretKey = null, status = form.getFieldValue('status'), permissionMappings = [], createdAt = null, expiredAt = null } = res?.data || {}
      setKeyData(secretKey)
      setEnable(status)
      setExpired(expiredAt && dayjs(expiredAt).isBefore(dayjs()))
      const mappingKey = permissionMappings.reduce((all, i) => ({ ...all, [`${i.apiKeyPermissionId}`]: i.mappingKeyId }), {})
      form.setFieldsValue({ secretKey, status, mappingKey, createdAt: formatDate(createdAt), expiredAt: formatDate(expiredAt) })
    }
  }

  const handleGenerate = async () => {
    const valid = await form.validateFields()
    if (!valid) return
    const { mappingKey } = valid
    const permissionMappings = Object.entries(mappingKey)?.map(([key, val]) => ({ apiKeyPermissionId: Number(key), mappingKeyId: Number(val) }))
    const res = await generateApiKey({ groupId: nowGroup, brandId: nowBrand, permissionMappings })
    if (res?.status === 200) getApiKey()
  }

  const handleRegenerate = async () => {
    const res = await regenerateApiKey(nowBrand)
    if (res?.status === 200) getApiKey()
  }

  const handleRevoke = async (title, okText, onCancel = () => {}) => Modal.confirm({
    title,
    content: <div style={{ margin: '8px 0' }}>
      {ln_t('apiKey.revokeDesc')}
      <Input value={keyData} disabled style={{ margin: '8px 0', textAlign: 'center' }} />
    </div>,
    icon: null,
    width: 600,
    okButtonProps: { danger: true },
    okText,
    onOk: async () => {
      const res = await revokeApiKey(nowBrand)
      if (res?.status === 204) getApiKey()
    },
    onCancel
  })

  const handleChangeStatus = e => {
    const status = e.target.value
    if (!status && keyData) handleRevoke(ln_t('apiKey.disableYourKey'), ln_t('util.disable'), () => form.setFieldValue('status', 1))
    else setEnable(status)
  }

  const getApiPermission = async () => {
    const res = await fetchApiKeyPermissions({ maxResults: 999 })
    if (res?.status === 200) setApiPermissionMapping(res?.data?.reduce((all, i) => ({ ...all, [`${i.id}`]: i }), {}))
  }

  const getMappingOptions = async () => {
    const res = await fetchMappingKeys({ maxResults: 999, groupIds: adminMode ? undefined : `0,${nowGroup}`, sort: 'groupId' })
    if (res?.status === 200) setMappingOpt(res?.data?.map(i => ({ value: i.id, label: i.name })))
  }

  useEffect(() => {
    getApiKey()
    getApiPermission()
    getMappingOptions()
  }, [])

  const resultProps = { disabled: true, className: keyData && enable ? 'white-disable' : '', style: { width: 'calc(100% - 80px)', textAlign: 'center' } }
  return <WhiteWrapper>
    <h3>{ln_t('apiKey.apiKey')}</h3>

    <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
      <Form form={form} layout="vertical" requiredMark={false}>
        <FormWrapper>
          <FormItemWrapper>
            <TwoRow>
              <div style={{ fontWeight: 600 }}>{ln_t('apiKey.apiKey')}</div>
              <div style={{ fontWeight: 600 }}>{ln_t('apiKey.mappingKeyId')}</div>
            </TwoRow>
            {Object.values(apiPermissionMapping)?.length
              ? apiKeyPermissions?.split(',')?.map(i => <TwoRow key={i}>
                  <div>{apiPermissionMapping[i]?.name}</div>
                  <Form.Item {...defConfig('mappingKeyId', 'input')} label="" name={['mappingKey', i]}>
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder={defPlaceholder('mappingKeyId', 'input')}
                      options={mappingOpt}
                      disabled={!enable || keyData}
                    />
                  </Form.Item>
                </TwoRow>)
              : <Spin style={{ margin: '40px', display: 'flex', justifyContent: 'center' }} />}
            <Button type="primary" style={{ marginLeft: 'auto', display: 'flex', marginBottom: '12px' }} onClick={handleGenerate} disabled={!enable || keyData}>
              {ln_t('apiKey.generateApiKey')}
            </Button>
            <Form.Item {...defConfig('status', 'input')} initialValue={0}>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                options={statusOpt}
                onChange={handleChangeStatus}
                block
              />
            </Form.Item>
          </FormItemWrapper>

          <FormItemWrapper right>
            <div style={{ display: 'flex' }}>
              <Form.Item {...defConfig('secretKey', 'input', false)} style={resultProps.style}>
                <Input {...resultProps} style={{ width: '100%', textAlign: 'center' }} />
              </Form.Item>
              <div style={{ marginTop: 'auto' }}>
                <Tooltip title={ln_t('apiKey.copy')}>
                  <CopyToClipboard text={keyData}>
                    <Button style={{ padding: '4px 8px', margin: 'auto 6px 24px 8px' }} disabled={!keyData || !enable}><CopyOutlined /></Button>
                  </CopyToClipboard>
                </Tooltip>
                <Tooltip title={ln_t('apiKey.regenerate')}>
                  <Button style={{ padding: '4px 8px', margin: 'auto 0 24px' }} disabled={!keyData || !enable || !expired} onClick={handleRegenerate}><ReloadOutlined /></Button>
                </Tooltip>
              </div>
            </div>
            <Form.Item {...defConfig('createdAt', 'input', false)}>
              <Input {...resultProps} />
            </Form.Item>
            <Form.Item {...defConfig('expiredAt', 'input', false)}>
              <Input {...resultProps} />
            </Form.Item>
            <Button type="primary" danger style={{ margin: '0 80px 0 auto', display: 'flex' }} disabled={!keyData} onClick={() => handleRevoke(ln_t('apiKey.revokeYourKey'), ln_t('apiKey.revoke'))}>
              {ln_t('apiKey.revoke')}
            </Button>
          </FormItemWrapper>
        </FormWrapper>
      </Form>
    </ConfigProvider>
  </WhiteWrapper>
}

export default ApiKey
